import { db } from '../firebase.js';
import {
    collection,
    doc,
    setDoc,
    getDocs,
    deleteField,
    query,
    where
} from 'firebase/firestore';

// Import modular services
import { userService } from './userService';
import { locationService } from './locationService';
import { libraryService } from './libraryService';
import { permitService } from './permitService';

// --- CONFIGURATION ---
const CONFIG = {
    useRemoteData: false, // OFF = Local Mode (Firebase/IndexedDB), ON = Remote API Mode
    apiEndpoint: "https://api.verzonnen-klant.nl/v1" // Placeholder
};

// --- INTERFACE DEFINITION (Implicit via Class Structure) ---
class IDataProvider {
    getAllUsers() { throw new Error("Method not implemented."); }
    saveUser(user, role) { throw new Error("Method not implemented."); }
    // Add all other methods...
}

// --- LOCAL PROVIDER (Actual Implementation) ---
class LocalDataProvider {
    constructor() {
    }

    // --- USER ---
    getAllUsers(scope) { return userService.getAllUsers(scope); }
    saveUser(user, role) { return userService.saveUser(user, role); }
    getUserProfile(uid) { return userService.getUserProfile(uid); }
    subscribeToUserProfile(uid, cb) { return userService.subscribeToUserProfile(uid, cb); }
    updateUserLocations(uid, locs) { return userService.updateUserLocations(uid, locs); }
    updateUserRole(uid, role) { return userService.updateUserRole(uid, role); }
    deleteUser(uid) { return userService.deleteUser(uid); }

    // --- LOCATION ---
    saveLocation(name, data) { return locationService.saveLocation(name, data); }
    getLocation(name) { return locationService.getLocation(name); }
    getAllLocations(scope) { return locationService.getAllLocations(scope); }
    deleteLocation(name) { return locationService.deleteLocation(name); }
    getLocations() { return locationService.getAllLocations(); } // Legacy alias

    // --- LIBRARY ---
    getGlobalLibrary() { return libraryService.getGlobalLibrary(); }
    getLocationLibrary(loc) { return libraryService.getLocationLibrary(loc); }
    saveLocationLibrary(loc, data) { return libraryService.saveLocationLibrary(loc, data); }
    getCombinedLibrary(loc) { return libraryService.getCombinedLibrary(loc); }
    updateLibraryTask(name, data, meta, loc) { return libraryService.updateLibraryTask(name, data, meta, loc); }
    deleteLibraryTask(name, loc) { return libraryService.deleteLibraryTask(name, loc); }
    resetLocationLibrary(loc) { return libraryService.resetLocationLibrary(loc); }
    saveGlobalLibrary(...args) { return libraryService.saveGlobalLibrary(...args); }
    getAllTaskImagesRaw() { return libraryService.getAllTaskImagesRaw(); }
    deleteTaskImage(id) { return libraryService.deleteTaskImage(id); }
    saveTaskImages(id, urls, locationId) { return libraryService.saveTaskImages(id, urls, locationId); }
    getTaskImages(id) { return libraryService.getTaskImages(id); }
    listAllStorageFiles(path) { return libraryService.listAllStorageFiles(path); }

    // --- PERMIT ---
    savePermit(data) { return permitService.savePermit(data); }
    getPermitsByStatus(status, scope) { return permitService.getPermitsByStatus(status, scope); }
    getPermitsByApplicant(uid) { return permitService.getPermitsByApplicant(uid); }
    getAllPermits(scope) { return permitService.getAllPermits(scope); }
    getAllAssignedPermits() { return permitService.getAllAssignedPermits(); }
    updatePermitStatus(...args) { return permitService.updatePermitStatus(...args); }
    assignPermit(...args) { return permitService.assignPermit(...args); }
    updatePermitChecklist(id, data) { return permitService.updatePermitChecklist(id, data); }
    saveLotoMetadata(id, meta) { return permitService.saveLotoMetadata(id, meta); }
    getPermitsForUser(ids) { return permitService.getPermitsForUser(ids); }
    deletePermit(id) { return permitService.deletePermit(id); }
    deletePermitsByTaskImage(name, url) { return permitService.deletePermitsByTaskImage(name, url); }
    getPermitsByComponent(loc, comp) { return permitService.getPermitsByComponent(loc, comp); }
    getPermitsByInstance(loc, hall, line, sub, inst) { return permitService.getPermitsByInstance(loc, hall, line, sub, inst); }
    updatePermitAssignment(id, uid, name, email) { return permitService.updatePermitAssignment(id, uid, name, email); }
    getLastApprovedLotoPhotos(...args) { return permitService.getLastApprovedLotoPhotos(...args); }
    checkIfApprovedPatternExists(data) { return permitService.checkIfApprovedPatternExists(data); }

    // --- MIGRATION (Local Specific) ---
    async migrateData(fullJsonData) {

        // 1. Save Library
        if (fullJsonData.onderdeel_tasks) {
            await libraryService.saveGlobalLibrary(
                fullJsonData.onderdeel_tasks,
                fullJsonData.task_metadata || {},
                fullJsonData.template_config || null,
                fullJsonData.global_tools || [],
                fullJsonData.global_ppes || []
            );
        }

        // 2. Save Locations
        if (fullJsonData.locations) {
            for (const [name, data] of Object.entries(fullJsonData.locations)) {
                await locationService.saveLocation(name, data);
            }
        }

        // 3. Save Users
        if (fullJsonData.users) {
            for (const user of fullJsonData.users) {
                await userService.saveUser(user, user.role);
            }
        }
    }
}

// --- REMOTE PROVIDER (The "Stekkerdoos" for future) ---
class RemoteDataProvider {
    constructor() {
    }

    // --- STUBS FOR API INTEGRATION ---
    async getAllUsers() {
        // return fetch(CONFIG.apiEndpoint + '/users').then(r => r.json());
        return [];
    }

    async savePermit(data) {
        // return fetch(CONFIG.apiEndpoint + '/permits', { method: 'POST', body: JSON.stringify(data) });
        return { id: "mock-api-id-123" };
    }

    // All other methods would throw or log "Not Connected"
    saveLocation() { console.warn("API Mode: Read-only for locations?"); }
    // ... Implement others as needed
}

// --- FACTORY / FACADE ---
// Decide which provider to use based on CONFIG
const currentProvider = CONFIG.useRemoteData ? new RemoteDataProvider() : new LocalDataProvider();

// Export the facade that simply delegates to the active provider
export const dataService = {
    // Utilities
    deleteField: deleteField,

    // Delegate all calls to the provider
    // USERS
    getAllUsers: (scope) => currentProvider.getAllUsers(scope),
    saveUser: (user, role) => currentProvider.saveUser(user, role),
    getUserProfile: (uid) => currentProvider.getUserProfile(uid),
    subscribeToUserProfile: (uid, cb) => currentProvider.subscribeToUserProfile(uid, cb),
    updateUserLocations: (uid, locs) => currentProvider.updateUserLocations(uid, locs),
    updateUserRole: (uid, role) => currentProvider.updateUserRole(uid, role),
    deleteUser: (uid) => currentProvider.deleteUser(uid),

    // LOCATIONS
    saveLocation: (name, data) => currentProvider.saveLocation(name, data),
    getLocation: (name) => currentProvider.getLocation(name),
    getAllLocations: (scope) => currentProvider.getAllLocations(scope),
    deleteLocation: (name) => currentProvider.deleteLocation(name),
    getLocations: () => currentProvider.getAllLocations(),

    // LOCATIONS - GRANULAR UPDATES
    addInstanceToHall: (loc, hall, line, sub, data) => locationService.addInstanceToHall(loc, hall, line, sub, data),
    removeInstanceFromHall: (loc, hall, line, sub, url) => locationService.removeInstanceFromHall(loc, hall, line, sub, url),
    updateInstancePhoto: (loc, hall, line, sub, oldUrl, newUrl) => locationService.updateInstancePhoto(loc, hall, line, sub, oldUrl, newUrl),

    // LIBRARY
    getGlobalLibrary: () => currentProvider.getGlobalLibrary(),
    getLocationLibrary: (loc) => currentProvider.getLocationLibrary(loc),
    saveLocationLibrary: (loc, data) => currentProvider.saveLocationLibrary(loc, data),
    getCombinedLibrary: (loc) => currentProvider.getCombinedLibrary(loc),
    updateLibraryTask: (name, data, meta, loc) => currentProvider.updateLibraryTask(name, data, meta, loc),
    deleteLibraryTask: (name, loc) => currentProvider.deleteLibraryTask(name, loc),
    resetLocationLibrary: (loc) => currentProvider.resetLocationLibrary(loc),
    saveGlobalLibrary: (...args) => currentProvider.saveGlobalLibrary(...args),
    getAllTaskImagesRaw: () => currentProvider.getAllTaskImagesRaw(),
    deleteTaskImage: (id) => currentProvider.deleteTaskImage(id),
    saveTaskImages: (id, urls, locationId) => currentProvider.saveTaskImages(id, urls, locationId),
    getTaskImages: (id) => currentProvider.getTaskImages(id),
    listAllStorageFiles: (path) => currentProvider.listAllStorageFiles ? currentProvider.listAllStorageFiles(path) : [],
    deleteImage: (url) => libraryService.deleteImage ? libraryService.deleteImage(url) : null,
    uploadImage: (file, path) => libraryService.uploadImage ? libraryService.uploadImage(file, path) : null,

    // CONFIGURATION
    saveConfigDisciplines: (disciplines) => currentProvider.saveGlobalLibrary(null, null, null, null, null, null, disciplines, null),
    saveConfigWaste: (waste) => currentProvider.saveGlobalLibrary(null, null, null, null, null, null, null, waste),
    saveMechanicChecksLibrary: (checks) => currentProvider.saveGlobalLibrary(null, null, null, null, null, checks, null, null),
    saveSystemSettings: (settings) => libraryService.saveSystemSettings(settings),

    // PERMITS
    savePermit: (data) => currentProvider.savePermit(data),
    getPermitsByStatus: (status, scope) => currentProvider.getPermitsByStatus(status, scope),
    getPermitsByApplicant: (uid) => currentProvider.getPermitsByApplicant(uid),
    getAllPermits: (scope) => currentProvider.getAllPermits(scope),
    getAllAssignedPermits: () => currentProvider.getAllAssignedPermits(),
    updatePermitStatus: (...args) => currentProvider.updatePermitStatus(...args),
    assignPermit: (...args) => currentProvider.assignPermit(...args),
    updatePermitChecklist: (id, data) => currentProvider.updatePermitChecklist(id, data),
    saveLotoMetadata: (id, metadata) => currentProvider.saveLotoMetadata(id, metadata),
    getPermitsForUser: (ids) => currentProvider.getPermitsForUser(ids),
    deletePermit: (id) => currentProvider.deletePermit(id),
    deletePermitsByTaskImage: (name, url) => currentProvider.deletePermitsByTaskImage(name, url),
    getPermitsByComponent: (loc, comp) => currentProvider.getPermitsByComponent(loc, comp),
    getPermitsByInstance: (loc, hall, line, sub, inst) => currentProvider.getPermitsByInstance(loc, hall, line, sub, inst),
    updatePermitAssignment: (id, uid, name, email) => currentProvider.updatePermitAssignment(id, uid, name, email),
    getLastApprovedLotoPhotos: (...args) => currentProvider.getLastApprovedLotoPhotos(...args),
    checkIfApprovedPatternExists: (data) => currentProvider.checkIfApprovedPatternExists(data),

    // MIGRATION
    migrateData: (data) => currentProvider.migrateData(data),

    // HIERARCHY UPDATES
    addInstanceToHall: (loc, hall, line, sub, data) => locationService.addInstanceToHall(loc, hall, line, sub, data),
    removeInstanceFromHall: (loc, hall, line, sub, url) => locationService.removeInstanceFromHall(loc, hall, line, sub, url),
    updateInstancePhoto: (loc, hall, line, sub, oldUrl, newUrl) => locationService.updateInstancePhoto(loc, hall, line, sub, oldUrl, newUrl),
    updateInstanceData: (loc, hall, line, sub, name, updates) => locationService.updateInstanceData(loc, hall, line, sub, name, updates),
    updatePermitsImageReference: (oldUrl, newUrl) => permitService.updatePermitsImageReference(oldUrl, newUrl),

    // LEGACY RESTORE
    async restoreDatabaseFromJSON() {
        try {
            const response = await fetch('/data/raconworks_data.json');
            if (!response.ok) throw new Error("Failed to fetch fallback data");
            const data = await response.json();
            await currentProvider.migrateData(data);
            return "Restore complete";
        } catch (e) {
            console.error("Restore failed:", e);
            throw e;
        }
    }
};
